import jsPDF from 'jspdf';
import moment from 'moment-timezone';

import { GAP, PAGE_COLOR } from 'lib/helpers/exportPdf/lib/constants';
import { generatePageNumberAndDate } from 'lib/helpers/exportPdf/lib/helpers';
import { printPrintWindow } from 'lib/helpers/exportPdf/lib/printPrintWindow';
import { generateHeader } from 'lib/helpers/exportPdf/pdfHeader';
import { ia, iaRa, removeHtmlTags } from 'lib/helpers/utility';

import { formatLabel, isEmptyOrBlank, renderValue } from '../../../lib/helpers';

const addPageIfNeeded = (doc, startY) => {
  const pageHeight = doc.internal.pageSize.height;
  const minBottomMargin = 30; // footer height

  if (startY > pageHeight - minBottomMargin) {
    doc.addPage();
    return GAP;
  }

  return startY;
};

// Helper to clean text for PDF (remove HTML, normalize whitespace)
const cleanText = (text) => {
  if (text === null || text === undefined || text === '' || text === 'blank' || text === 'null') {
    return text;
  }

  let cleaned = removeHtmlTags(String(text), { preserveSpacing: false });
  cleaned = cleaned.replace(/\n/g, ' ');
  cleaned = cleaned.replace(/\s+/g, ' ');
  return cleaned.trim();
};

// Format date/time like "11/17/25, 07:01 am"
const formatShortDateTime = (dateString) => {
  if (!dateString) return '';
  const date = moment(dateString);
  return date.format('MM/DD/YY, hh:mm a').toLowerCase();
};

const formatActivityForPdf = (activity) => {
  const { creator, created_at, content, action } = activity;
  const fullName = creator ? `${creator.f_name} ${creator.l_name}` : 'Unknown';
  const dateTime = formatShortDateTime(created_at);

  const { previousValues = {}, currentValues = {} } = content || {};
  const previousKeys = Object.keys(previousValues);
  const currentKeys = Object.keys(currentValues);

  const changes = [];
  const isDelete = action === 'delete';
  const isCreate = action === 'create';

  if (previousKeys.length > 0) {
    previousKeys.forEach((key) => {
      const prevValueRaw = renderValue(key, previousValues[key]);
      const currValueRaw = renderValue(key, currentValues[key]);
      const prevValue = cleanText(prevValueRaw);
      const currValue = cleanText(currValueRaw);

      if (prevValue !== currValue) {
        const label = formatLabel(key);
        const isNestedCreate = isCreate || isEmptyOrBlank(previousValues[key]);

        if (isNestedCreate) {
          // For creates, just show the new value
          changes.push(`${label}: ${currValue || 'blank'}`);
        } else if (isDelete) {
          // For deletes, show what was removed
          changes.push(`Changed ${label} from '${prevValue || 'blank'}' to blank.`);
        } else {
          // For updates, show from → to
          changes.push(
            `Changed ${label} from '${prevValue || 'blank'}' to '${currValue || 'blank'}'.`
          );
        }
      }
    });
  } else if (currentKeys.length > 0) {
    currentKeys.forEach((key) => {
      const currValueRaw = renderValue(key, currentValues[key]);
      const currValue = cleanText(currValueRaw);
      const label = formatLabel(key);
      changes.push(`${label}: ${currValue || 'blank'}`);
    });
  }

  return {
    dateTime,
    creator: fullName,
    changes,
    isComment: false,
    action
  };
};

const formatCommentForPdf = (comment) => {
  const { creator, created_at, updated_at, value } = comment;
  const fullName = creator ? `${creator.f_name} ${creator.l_name}` : 'Unknown';
  const dateTime = formatShortDateTime(created_at);
  const commentText = cleanText(value || '');
  const isEdited = updated_at && created_at !== updated_at;

  return {
    dateTime,
    creator: fullName,
    changes: [commentText ? `${commentText}${isEdited ? ' (edited)' : ''}` : 'Comment: (empty)'],
    isComment: true
  };
};

export const exportActivitiesToPdf = (activities, title = 'Activity Log', practice = null) => {
  if (!activities || !ia(activities) || activities.length === 0) {
    return;
  }

  const formattedActivities = iaRa(activities)
    .map((item) => {
      if (item.type === 'comment') {
        return formatCommentForPdf(item);
      }
      return formatActivityForPdf(item);
    })
    .filter((activity) => activity.changes.length > 0);

  if (formattedActivities.length === 0) {
    return;
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;

  // Set PDF metadata
  const fileName = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.setProperties({ title: fileName });

  // Generate header using the same utility as printPdf
  let startY = generateHeader({ doc, headerConfigs: { practice } });
  startY += 5;

  // Add centered title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(PAGE_COLOR);
  const titleWidth = doc.getTextWidth(title);
  const titleX = (pageWidth - titleWidth) / 2;
  doc.text(title, titleX, startY);
  startY += 10;

  // Add activities in list style (like the image)
  const lineHeight = GAP / 2;

  formattedActivities.forEach((activity, index) => {
    // Check if we need a new page
    startY = addPageIfNeeded(doc, startY);

    // Determine background color for this activity (alternate between activities)
    const isEvenActivity = (index + 1) % 2 === 0;
    const backgroundColor = isEvenActivity ? [245, 245, 245] : null; // Light gray for even, null (white) for odd

    // Draw background for date/time + actor line if activity has background color
    if (backgroundColor) {
      doc.setFillColor(...backgroundColor);
      doc.rect(0, startY - lineHeight * 0.8, pageWidth, lineHeight + 1, 'F');
    }

    // Format: Date/Time, Actor, then changes
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    // Date/Time first (e.g., "11/17/25, 07:01 am")
    doc.text(activity.dateTime, GAP, startY);
    const currentX = GAP + doc.getTextWidth(activity.dateTime) + 5;

    // Actor name
    const actorText = activity.isComment
      ? `${activity.creator} commented:`
      : activity.creator + (activity.action === 'create' ? ' created:' : '');
    doc.setFont('helvetica', 'bold');
    doc.text(actorText, currentX, startY);
    startY += lineHeight;

    // Calculate available width for change text (from currentX to right margin)
    const availableWidth = pageWidth - currentX - GAP;

    // Each change on its own line
    doc.setFont('helvetica', 'normal');
    activity.changes.forEach((change) => {
      // Check if we need a new page for this change
      startY = addPageIfNeeded(doc, startY);

      // Calculate height for this change line - use available width to prevent overflow
      const splitText = doc.splitTextToSize(change, availableWidth);
      const changeHeight = splitText.length * lineHeight;

      // Draw background for this change line if activity has background color
      if (backgroundColor) {
        doc.setFillColor(...backgroundColor);
        doc.rect(0, startY - lineHeight * 0.8, pageWidth, changeHeight + 1, 'F');
      }

      // Split long text to fit available width and draw text on top of background
      doc.text(splitText, currentX, startY);
      startY += changeHeight;
    });

    // Add spacing between entries
    if (index < formattedActivities.length - 1) {
      startY += GAP / 2;
    }
  });

  generatePageNumberAndDate(doc);

  // Use the same print utility as printPdf
  printPrintWindow(doc);
};
